import {
  ComponentType,
  default as React,
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useSelector } from 'react-redux'
import useThunkDispatch from '../common/useThunkDispatch'
import { Navigate } from 'react-router-dom'
import AutoSizer from 'react-virtualized-auto-sizer'
import {
  FixedSizeList,
  ListChildComponentProps,
  ListItemKeySelector,
} from 'react-window'
import useTitle from 'react-use/lib/useTitle'
import { format } from 'date-fns'

import {
  ordersFilterSelector,
  ordersPaginationStateSelector,
  ordersSelector,
  ordersTotalSelector,
} from './ordersSelectors'
import OrderStatus from './OrderStatus'
import OrderIcon from './OrderIcon'
import name from '../common/name'
import { ICursoryOrder, isSubmittedOrder } from '../types/orders'
import { filterOrders, FilterOrdersReq } from './ordersActions'
import OrdersFilter from './OrdersFilter'
import Splash from '../components/Splash'
import Pager from '../components/Pager'

const PER_PAGE = 50

const Orders: FunctionComponent = () => {
  useTitle('Orders - Forshaw')

  const dispatch = useThunkDispatch()

  const listRef = useRef<FixedSizeList>(null)

  const [loading, setLoading] = useState(false)

  const orders = useSelector(ordersSelector)
  const total = useSelector(ordersTotalSelector)
  const paginationState = useSelector(ordersPaginationStateSelector)
  const ordersFilter = useSelector(ordersFilterSelector)
  const [page, setPage] = useState(0)

  const [openedOrder, setOpenedOrder] = useState<ICursoryOrder | undefined>(
    undefined,
  )

  const loadOrders = async (req: FilterOrdersReq) => {
    setLoading(true)
    await dispatch(filterOrders(req))
    setLoading(false)
  }

  useEffect(() => {
    if (listRef.current) listRef.current.scrollToItem(0)
    setPage(0)
    loadOrders({ first: PER_PAGE, ...ordersFilter })
  }, [ordersFilter])

  if (openedOrder) return <Navigate to={`/orders/${openedOrder.id}`} replace />

  const Row: ComponentType<ListChildComponentProps> = ({ index, style }) => {
    const order = orders[index]

    return (
      <div style={style}>
        <div
          className="table_tr -fixed-height -link"
          onClick={() => setOpenedOrder(order)}
        >
          <div className="table_td -fix50">
            <OrderIcon order={order} />
          </div>
          <div className="table_td -g25">
            {isSubmittedOrder(order) && format(order.submittedAt, 'MM/DD/YYYY')}
          </div>
          <div className="table_td -g25">{order.account.name}</div>
          <div className="table_td -g25">{order.branch.name}</div>
          <div className="table_td -g25">{order.navOrderId}</div>
          <div className="table_td -g25">
            {isSubmittedOrder(order) && name(order.submittedBy)}
          </div>
        </div>
      </div>
    )
  }
  const ItemKey: ListItemKeySelector = (index) => orders[index].id

  const handlePrevious = async () => {
    await loadOrders({
      last: PER_PAGE,
      before: paginationState.startCursor,
      ...ordersFilter,
    })
    setPage(page - 1)
  }

  const handleNext = async () => {
    await loadOrders({
      first: PER_PAGE,
      after: paginationState.endCursor,
      ...ordersFilter,
    })
    setPage(page + 1)
  }

  return (
    <div className="flexible-container table-page-layout">
      <OrdersFilter />
      <div className="table">
        <div className="table_head-tr">
          <div className="table_td -fix50" />
          <div className="table_td table_title -g25">Submitted</div>
          <div className="table_td table_title -g25">Client</div>
          <div className="table_td table_title -g25">Branch</div>
          <div className="table_td table_title -g25">Nav order ID</div>
          <div className="table_td table_title -g25">Submitted by</div>
        </div>
      </div>

      <div className="table_loading-wrapper">
        <AutoSizer className="table">
          {(size) => (
            <FixedSizeList
              ref={listRef}
              className="wonder-scroll"
              {...size}
              itemCount={orders.length}
              itemSize={55 + 17}
              itemKey={ItemKey}
            >
              {Row}
            </FixedSizeList>
          )}
        </AutoSizer>
        <Splash isLoading={loading} bgColor="#F2F6FA" />
      </div>

      <Pager
        page={page}
        total={total}
        perPage={PER_PAGE}
        previousEnabled={paginationState.hasPreviousPage && !loading}
        nextEnabled={paginationState.hasNextPage && !loading}
        onNext={handleNext}
        onPrevious={handlePrevious}
        className="-right"
      />
    </div>
  )
}

export default Orders
